import { useMemo, useState } from "react";
import { toast } from "sonner";
import { QuestionInterface } from "../../../domain/interfaces/question_interface";
import TextAreaComment from "./textarea_comment";
import StarButton from "./star_button";
import { useStarRating } from "../../../domain/hooks/use_star_rating";
import { AnswerInterface } from "../../../domain/interfaces/answer_interface";
import { useTranslation } from "../../../../../i18n/useTranslation";

interface NumberQuestionProps {
    question: QuestionInterface;
    color: string;
    onValidate: (answer: AnswerInterface, initialAnswer?: AnswerInterface) => void;
}

const STARS_COUNT = 5;
const STAR_NUMBERS = Array.from({ length: STARS_COUNT }, (_, i) => i + 1);

const CONTAINER_STYLES = {
    touchAction: 'none' as const,
    userSelect: 'none' as const,
    WebkitUserSelect: 'none' as const,
    MozUserSelect: 'none' as const,
    msUserSelect: 'none' as const,
    WebkitTouchCallout: 'none' as const,
    WebkitTapHighlightColor: 'transparent'
};

const BUTTON_BASE_STYLES = {
    userSelect: 'none' as const,
    WebkitUserSelect: 'none' as const,
    MozUserSelect: 'none' as const,
    msUserSelect: 'none' as const,
    WebkitTouchCallout: 'none' as const,
    WebkitTapHighlightColor: 'transparent'
};

const NumberQuestion: React.FC<NumberQuestionProps> = ({ question, color, onValidate }) => {
    const { t } = useTranslation();
    const {
        selectedStars,
        hoveredStar,
        isDragging,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        handleStarClick,
        handlePointerEnter,
        handlePointerLeave,
    } = useStarRating(question.initialAnswer?.answer as number | undefined);

    const [ comment, setComment ] = useState<string>(question.initialAnswer?.text || '');

    const questionStyle = useMemo(() => ({
        color: color,
        boxShadow: '0 0 4px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.08)',
    }), [color]);

    const buttonStyle = useMemo(() => ({
        ...BUTTON_BASE_STYLES,
        pointerEvents: isDragging ? 'none' as const : 'auto' as const,
    }), [isDragging]);

    return (
        <div className="flex flex-col items-center gap-5 max-w-94.5 w-full">
            <p
                className="bg-white font-bold text-xl leading-tight px-9 py-4 text-center rounded-[20px] aspect-3/2 flex items-center justify-center w-full"
                style={questionStyle}
            >
                {question.question}
            </p>

            <form
                id={`question-${question.id}`}
                className="flex items-center justify-between w-full gap-5"
                onTouchStart={handleTouchStart}
                onTouchMove={(e) => handleTouchMove(e, STARS_COUNT)}
                onTouchEnd={handleTouchEnd}
                onSubmit={(e) => {
                    e.preventDefault();
                    if (selectedStars === 0) {
                        toast.error(t('survey.answer_required'));
                        return;
                    }
                    onValidate({
                        questionId: question.id,
                        answer: selectedStars,
                        text: comment
                    }, question.initialAnswer);
                }}
                style={CONTAINER_STYLES}
            >
                {STAR_NUMBERS.map((starNumber) => {
                    const isSelected = selectedStars >= starNumber;
                    const isHovered = hoveredStar >= starNumber;
                    const shouldShowYellow = (isSelected && hoveredStar === 0) || (hoveredStar > 0 && isHovered);

                    return (
                        <StarButton
                            key={starNumber}
                            starNumber={starNumber}
                            isSelected={isSelected}
                            isHovered={isHovered}
                            shouldShowYellow={shouldShowYellow}
                            onClick={handleStarClick(starNumber)}
                            onPointerEnter={handlePointerEnter(starNumber)}
                            onPointerLeave={handlePointerLeave()}
                            buttonStyle={buttonStyle}
                        />
                    );
                })}
            </form>

            <TextAreaComment comment={comment} onCommentChange={(value: string) => setComment(value)} />
        </div>
    );
}

export default NumberQuestion;